import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { VEHICLES, SERVICE_META } from "@/components/site/Services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_DURATION = {
  basic_interior_wash: "~60 min",
  basic_interior: "~90 min",
  interior_exterior: "~3 hrs",
  caravan_exterior: "~1.5 hrs",
  caravan_interior: "~3 hrs",
  caravan_full: "~4.5 hrs",
};

const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00"];

const STEPS = ["Vehicle", "Service", "Schedule", "Details"];

const findVehicle = (id) => VEHICLES.find((v) => v.id === id);
const defaultServiceFor = (sizeId) => {
  const v = findVehicle(sizeId);
  return v ? Object.keys(v.services)[0] : "";
};

export default function Booking() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    vehicle_size: "sedan",
    service_id: "basic_interior",
    preferred_date: undefined,
    preferred_time: "",
    name: "",
    email: "",
    phone: "",
    vehicle_make: "",
    vehicle_model: "",
    address: "",
    notes: "",
  });

  // Listen to Service cards prefill
  useEffect(() => {
    const handler = () => {
      const p = window.__abPrefill;
      if (!p) return;
      setForm((f) => ({ ...f, ...p }));
      setStep(2);
    };
    window.addEventListener("ab-prefill", handler);
    return () => window.removeEventListener("ab-prefill", handler);
  }, []);

  const currentVehicle = findVehicle(form.vehicle_size);
  const availableServices = currentVehicle ? Object.entries(currentVehicle.services) : [];
  const price = currentVehicle?.services?.[form.service_id];

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When vehicle changes, reset to a valid service for that vehicle
  const pickVehicle = (id) => {
    setForm((f) => {
      const v = findVehicle(id);
      const validService = v && f.service_id in v.services ? f.service_id : defaultServiceFor(id);
      return { ...f, vehicle_size: id, service_id: validService };
    });
  };

  const canNext = () => {
    if (step === 0) return !!form.vehicle_size;
    if (step === 1) return !!form.service_id && price !== undefined;
    if (step === 2) return !!form.preferred_date && !!form.preferred_time;
    return true;
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone || !form.vehicle_make || !form.vehicle_model || !form.address) {
      toast.error("Please fill in all required fields.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        ...form,
        preferred_date: format(form.preferred_date, "yyyy-MM-dd"),
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      setSuccess(data);
      toast.success("Booking received! Check your email for confirmation.");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not submit booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section id="booking" data-testid="booking-section" className="py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div
            data-testid="booking-success"
            className="bg-[#0a0a0a] border border-white/15 rounded-2xl p-10 text-center"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-gold/15 ring-1 ring-gold/40 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-gold" strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-black mt-6">Booking received</h3>
            <p className="text-neutral-400 mt-3">
              Thanks {success.name} — we've emailed you a confirmation at{" "}
              <span className="text-white">{success.email}</span>.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3 text-left">
              <Row label="Service" value={success.service_name} />
              <Row label="Vehicle" value={`${success.vehicle_make} ${success.vehicle_model}`} />
              <Row label="Date" value={success.preferred_date} />
              <Row label="Time" value={success.preferred_time} />
              <Row label="Price" value={`$${success.price} NZD`} />
              <Row label="Reference" value={success.id.slice(0, 8).toUpperCase()} />
            </div>
            <button
              onClick={() => {
                setSuccess(null);
                setStep(0);
              }}
              data-testid="booking-again-btn"
              className="mt-10 inline-flex items-center gap-2 border border-white/20 hover:border-white/50 px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold"
            >
              Book another
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" data-testid="booking-section" className="py-24 sm:py-32 relative">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.25em] text-gold">04 — Book</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]">
          Reserve your detail.
        </h2>
        <p className="mt-5 text-neutral-400 max-w-xl">
          Four quick steps. We'll confirm by email — usually within the hour.
        </p>

        {/* Stepper */}
        <div className="mt-10 flex gap-3 sm:gap-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div
                className={`h-1 ${i <= step ? "bg-gold" : "bg-white/10"} transition-colors`}
              />
              <div
                data-testid={`step-label-${i}`}
                className={`mt-2 text-[10px] uppercase tracking-[0.18em] ${
                  i <= step ? "text-gold" : "text-neutral-600"
                }`}
              >
                {String(i + 1).padStart(2, "0")} · {s}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 sm:p-10">
          {step === 0 && (
            <div data-testid="step-vehicle">
              <h3 className="font-display text-2xl font-bold">What are you bringing in?</h3>
              <p className="text-neutral-400 mt-1.5 text-sm">Pick the vehicle class closest to yours.</p>
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {VEHICLES.map((v) => {
                  const fromPrice = Math.min(...Object.values(v.services));
                  return (
                    <button
                      key={v.id}
                      type="button"
                      data-testid={`pick-size-${v.id}`}
                      onClick={() => pickVehicle(v.id)}
                      className={`text-left p-5 rounded-xl border transition-all ${
                        form.vehicle_size === v.id
                          ? "border-gold bg-gold/5"
                          : "border-white/10 hover:border-gold/40"
                      }`}
                    >
                      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Class</div>
                      <div className="font-display font-bold text-lg mt-1">{v.label}</div>
                      <div className="text-sm text-neutral-400 mt-2">
                        From ${fromPrice}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-service">
              <h3 className="font-display text-2xl font-bold">Choose a service.</h3>
              <p className="text-neutral-400 mt-1.5 text-sm">
                Pricing shown for{" "}
                <span className="text-white">
                  {currentVehicle?.label}
                </span>
                .
              </p>
              <div className="mt-6 grid gap-3">
                {availableServices.map(([sid, p]) => {
                  const meta = SERVICE_META[sid];
                  const active = form.service_id === sid;
                  return (
                    <button
                      key={sid}
                      type="button"
                      data-testid={`pick-service-${sid}`}
                      onClick={() => update("service_id", sid)}
                      className={`flex items-center justify-between gap-6 p-5 rounded-xl border text-left transition-all ${
                        active ? "border-gold bg-gold/5" : "border-white/10 hover:border-gold/40"
                      }`}
                    >
                      <div>
                        <div className="font-display font-bold text-lg">{meta.name}</div>
                        <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mt-1">
                          {SERVICE_DURATION[sid] || ""}
                        </div>
                      </div>
                      <div className="font-display text-3xl font-black text-gold">${p}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-schedule" className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-display text-2xl font-bold">Pick a date.</h3>
                <p className="text-neutral-400 mt-1.5 text-sm">We're open Tue, Thu &amp; Sat · 9am – 3pm. We come to you.</p>
                <div className="mt-5 inline-block bg-[#0d0d0d] border border-white/10 rounded-xl p-2">
                  <Calendar
                    mode="single"
                    selected={form.preferred_date}
                    onSelect={(d) => update("preferred_date", d)}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      // Open Tue (2), Thu (4), Sat (6) only.
                      const day = d.getDay();
                      return d < today || ![2, 4, 6].includes(day);
                    }}
                    data-testid="booking-calendar"
                  />
                </div>
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold">Pick a time.</h3>
                <p className="text-neutral-400 mt-1.5 text-sm">Approximate start — we'll confirm exactly.</p>
                <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`time-${t}`}
                      onClick={() => update("preferred_time", t)}
                      className={`py-3 text-sm border transition-all ${
                        form.preferred_time === t
                          ? "border-gold bg-gold text-black"
                          : "border-white/10 hover:border-gold/60"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-details" className="grid md:grid-cols-2 gap-6">
              <Field label="Your name" required>
                <Input
                  data-testid="input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Jane Doe"
                  className="bg-[#0d0d0d] border-white/10 h-12"
                />
              </Field>
              <Field label="Phone" required>
                <Input
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="020 123 4567"
                  className="bg-[#0d0d0d] border-white/10 h-12"
                />
              </Field>
              <Field label="Email" required className="md:col-span-2">
                <Input
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                  className="bg-[#0d0d0d] border-white/10 h-12"
                />
              </Field>
              <Field label="Vehicle make" required>
                <Input
                  data-testid="input-make"
                  value={form.vehicle_make}
                  onChange={(e) => update("vehicle_make", e.target.value)}
                  placeholder="Toyota"
                  className="bg-[#0d0d0d] border-white/10 h-12"
                />
              </Field>
              <Field label="Vehicle model" required>
                <Input
                  data-testid="input-model"
                  value={form.vehicle_model}
                  onChange={(e) => update("vehicle_model", e.target.value)}
                  placeholder="Corolla"
                  className="bg-[#0d0d0d] border-white/10 h-12"
                />
              </Field>
              <Field label="Service address (we come to you)" required className="md:col-span-2">
                <Input
                  data-testid="input-address"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  placeholder="123 Riccarton Rd, Christchurch"
                  className="bg-[#0d0d0d] border-white/10 h-12"
                />
              </Field>
              <Field label="Anything we should know?" className="md:col-span-2">
                <Textarea
                  data-testid="input-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Pet hair, mud, location preferences, etc."
                  rows={4}
                  className="bg-[#0d0d0d] border-white/10"
                />
              </Field>

              <div className="md:col-span-2 bg-black/40 border border-white/10 rounded-xl p-5 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Summary</div>
                  <div className="mt-1 text-neutral-200">
                    {SERVICE_META[form.service_id]?.name} ·{" "}
                    {currentVehicle?.label} ·{" "}
                    {form.preferred_date ? format(form.preferred_date, "EEE d MMM") : "—"} ·{" "}
                    {form.preferred_time || "—"}
                  </div>
                </div>
                <div className="font-display text-3xl font-black text-gold">${price} <span className="text-sm text-neutral-500 font-medium">NZD</span></div>
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between gap-3">
            <button
              type="button"
              data-testid="booking-back-btn"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-400 hover:text-white disabled:opacity-30"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.6} /> Back
            </button>

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                data-testid="booking-next-btn"
                onClick={() => canNext() && setStep((s) => s + 1)}
                disabled={!canNext()}
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Continue <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </button>
            ) : (
              <button
                type="button"
                data-testid="booking-submit-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-gold text-black hover:brightness-110 px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-40 transition-all"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting
                  </>
                ) : (
                  <>
                    Confirm booking <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">
        {label} {required && <span className="text-white/70">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="bg-black/40 border border-white/10 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
      <div className="font-display mt-1 text-base">{value}</div>
    </div>
  );
}
